import React from 'react';
import cn from 'clsx';

import Button from '../../../../library/button/button';
import Quantity from '../../../../library/quantity/quantity';
import AddProductButton from '../../../../components/add-product-button/add-product-button';
import Price from '../../../../components/price/price';
import QuantityDisclaimer from '../../../../components/quantity-disclaimer/quantity-disclaimer';
import StickyFooter from '../../../appointment-page/components/sticky-footer/sticky-footer';
import {
  MinusRegularIcon,
  PlusRegularIcon
} from '../../../../shared/icons/icons';

import {
  useProductEntryDataLayerValues,
  useTireWheelPackage
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';

import { Fitment } from '../../../../shared/types/fitment';
import { ProductItem } from '../../../../shared/types/product';

import styles from './sticky-add-to-cart-section.one-dt.scss';

interface StickyAddToCartSectionProps {
  className?: string;
  defaultQuantity: number;
  isCanonicalProduct?: boolean;
  isDisabled?: boolean;
  isSet?: boolean;
  isStaggered?: boolean;
  price: number;
  productItem: ProductItem;
  productType: string;
  selectedFitment?: Fitment;
  storeCode?: string | null;
  updateQuantity: (e: Event, quantity: number) => void;
}

function StickyAddToCartSection(props: StickyAddToCartSectionProps) {
  const {
    className = '',
    defaultQuantity,
    isCanonicalProduct = false,
    isDisabled = false,
    isSet = false,
    isStaggered = false,
    price,
    productItem,
    productType,
    selectedFitment,
    storeCode,
    updateQuantity
  } = props;

  const { prebuiltPackage, product, quantity } = productItem;
  const { mapDisplayRule, mapPrice, mapRuleSatisfied, priceRange, unitName } =
    product || {};

  const { currentTireWheelPackageFlow } = useTireWheelPackage();

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  let currQuantity = quantity || defaultQuantity;

  function handleIncrease(e) {
    const newValue = currQuantity + 1;

    if (updateQuantity) {
      updateQuantity(e, newValue);
    }
  }

  function handleDecrease(e) {
    const newValue = currQuantity - 1;

    if (updateQuantity) {
      updateQuantity(e, newValue);
    }
  }

  function getQtyAndPriceClassName() {
    let className = 'qty-and-price';

    if (isCanonicalProduct) {
      return className + '-canonical';
    } else if (currentTireWheelPackageFlow) {
      return className + '-byo-package';
    } else {
      return className + '-standard';
    }
  }

  return (
    <StickyFooter className={cn(styles['container'], className)}>
      {!isStaggered && !isSet && (
        <div className={styles[getQtyAndPriceClassName()]}>
          <Price
            mapPrice={{
              mapPriceDisplay: mapDisplayRule?.mapPriceDisplay,
              mapRuleSatisfied,
              price: mapPrice?.value
            }}
            price={price}
            priceRange={priceRange}
            unitName={unitName}
            variant="P"
          />
          {(selectedFitment && isCanonicalProduct && !priceRange) ||
          !isCanonicalProduct ? (
            <>
              {currentTireWheelPackageFlow ? (
                <div className={styles['static-quantity']}>
                  <Quantity
                    aria-label="static-quantity"
                    defaultValue={defaultQuantity}
                    id="quantity"
                    isDisabled
                    isHorizontal
                    max={99}
                    min={1}
                  />
                </div>
              ) : (
                <div className={styles['quantity-buttons']}>
                  <Button
                    className={
                      currQuantity < 2
                        ? styles['button-disabled']
                        : styles['button']
                    }
                    isDisabled={currQuantity < 2}
                    onClick={handleDecrease}
                  >
                    <MinusRegularIcon />
                  </Button>

                  <Quantity
                    aria-label="quantity-input"
                    defaultValue={defaultQuantity}
                    id="quantity"
                    isHorizontal
                    max={99}
                    min={1}
                    onBlur={updateQuantity}
                    onChange={updateQuantity}
                    value={currQuantity}
                  />

                  <Button
                    className={
                      currQuantity > 98
                        ? styles['button-disabled']
                        : styles['button']
                    }
                    isDisabled={currQuantity > 98}
                    onClick={handleIncrease}
                  >
                    <PlusRegularIcon />
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      <AddProductButton
        hideSubtotal
        isDisabled={isDisabled}
        onClick={() =>
          trackAction(
            'product_detail_cart_add',
            getProductEntryDataLayerValues(product, currQuantity)
          )
        }
        productItem={productItem}
        storeCode={storeCode}
      />

      {!prebuiltPackage && !isStaggered && (
        <QuantityDisclaimer
          className={styles['quantity-disclaimer']}
          productType={productType}
          quantity={currQuantity}
        />
      )}
    </StickyFooter>
  );
}

export default StickyAddToCartSection;
